/** The interface between the dashboard's host page and the Bezettingsgraad renderer. Change it only through the lead. */

export const BEZETTING_TABS = [
  "overzicht",
  "kranen",
  "bezetting",
  "werkgebied",
  "klanten",
] as const;
export type BezettingTab = (typeof BEZETTING_TABS)[number];
export const BEZETTING_DEFAULT_TAB: BezettingTab = "overzicht";
export const BEZETTING_PATH = "/apps/bezetting";
export const BEZETTING_ASSET_BASE = "/apps-static/bezetting/";

/** One snapshot section as the API serves it: compact tables {cols, rows}, ISO dates, shares as fractions. */
export type BezettingSection = { readonly section: string } & Readonly<Record<string, unknown>>;

export interface BezettingSnapshotRef {
  readonly id: string;
  /** The snapshot day in Europe/Brussels, "2026-10-21". */
  readonly asOf: string;
  /** When Odoo was read, ISO UTC. */
  readonly odooReadAt: string;
  readonly computedAt: string;
  /** Every section key this snapshot holds. */
  readonly sections: readonly string[];
}

export interface BezettingSectionLoader {
  /** A section of the mounted snapshot, fetched once; rejects when it cannot be read. */
  load(key: string): Promise<BezettingSection>;
  /** The same, or null when this snapshot has no such section. */
  tryLoad(key: string): Promise<BezettingSection | null>;
}

export type MeldingRule = "geen_vrij" | "voorbij" | "langer_vrij";

export interface Melding {
  readonly key: string;
  readonly rule: MeldingRule;
  readonly subject_type: "type" | "crane";
  readonly subject_id: string;
  readonly subject: string;
  readonly episode: string;
  readonly first_seen: string;
  readonly values: Readonly<Record<string, unknown>>;
  readonly readAt: string | null;
  readonly read: boolean;
  /** Read on the current Brussels day: its block reads "Gelezen" with "Ongedaan maken". */
  readonly readToday: boolean;
  /** Read before today: no block. */
  readonly hidden: boolean;
}

export interface MeldingenFilter {
  readonly rule?: MeldingRule | null;
}

/** The read state shared by everyone at De Ceuster. The same instance for the life of a mount. */
export interface MeldingenStore {
  items(): readonly Melding[];
  list(opts?: MeldingenFilter & { readonly showRead?: boolean }): readonly Melding[];
  count(opts?: MeldingenFilter): number;
  readToday(opts?: MeldingenFilter): number;
  isRead(key: string | readonly string[]): boolean;
  /** Optimistic: emits at once, then saves; a failed save rolls back and emits again. */
  markRead(key: string | readonly string[]): void;
  markUnread(key: string | readonly string[]): void;
  subscribe(listener: (store: MeldingenStore) => void): () => void;
  /** Today in Europe/Brussels, "2026-10-25". */
  today(): string;
  size(): number;
}

export type RecomputeState =
  | { readonly kind: "idle" }
  | {
      readonly kind: "running";
      readonly jobId: string;
      readonly startedAt: string;
      /** How far the whole recompute is, 0 to 1; null while that is not known. */
      readonly fraction: number | null;
    }
  | { readonly kind: "waiting"; readonly until: string }
  | { readonly kind: "unavailable" };

export interface RecomputeHook {
  state(): RecomputeState;
  /** Starts a recompute when idle; does nothing otherwise. Errors reach the host's banner. */
  start(): void;
  /**
   * Shows what the running recompute is doing (the host's dialog). Absent where the host has no such view: the
   * button then stays disabled while a recompute runs.
   */
  showProgress?(): void;
  subscribe(listener: (state: RecomputeState) => void): () => void;
}

export interface BezettingQuery {
  /** The app URL's current query string. */
  read(): URLSearchParams;
  /** Writes the query string: push adds a history entry, otherwise the current entry is replaced. */
  write(params: URLSearchParams, opts?: { readonly push?: boolean }): void;
}

export interface BezettingDeps {
  readonly tab: BezettingTab;
  readonly snapshot: BezettingSnapshotRef;
  readonly sections: BezettingSectionLoader;
  readonly meldingen: MeldingenStore;
  /** Client id to name, from the namen section; shown to everyone who may open the app. */
  readonly names: ReadonlyMap<number, string>;
  readonly recompute: RecomputeHook;
  readonly query: BezettingQuery;
  /** Router navigation to another tab with its query (push by default). */
  navigate(tab: BezettingTab, params?: URLSearchParams, opts?: { readonly push?: boolean }): void;
  onError(error: unknown): void;
}

/**
 * What changed outside the renderer. `search` is the new query string without "?". A search equal to the
 * one the renderer last wrote changes nothing; a new snapshot comes with its own sections loader.
 */
export type BezettingUpdate = Partial<
  Pick<BezettingDeps, "tab" | "snapshot" | "sections" | "names">
> & {
  readonly search?: string;
};

export interface BezettingHandle {
  update(next: BezettingUpdate): void;
  /** Removes every element, listener, observer and timer the mount added; the element can be mounted again. */
  destroy(): void;
}

export type MountBezetting = (el: HTMLElement, deps: BezettingDeps) => BezettingHandle;
